import { NOTE_REQUIRED, type RequestEvent, type RequestStatus, type ReviewDetail } from "@procuremate/shared";
import { ArrowLeft } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { SelectField } from "@/components/ui/select-field";
import { useCurrentUser } from "@/features/auth/api";
import { useFieldCatalogue } from "@/features/requests/api";
import { DocumentList } from "@/features/requests/document-list";
import { DetailsBySection, FieldRow, FindingList } from "@/features/requests/request-panel";
import { RequestStatusBadge } from "@/features/requests/request-status-badge";
import { ApiRequestError } from "@/lib/api-client";
import { useAddComment, useAssign, useAssignees, useReview, useTransition } from "./api";
import { SourcingSection } from "./sourcing-section";

/**
 * A request as the buyer reviews it (WP-3.1, ADR-0022): who raised it and where it sits, what may be
 * done now, the details, documents, points for the buyer, the conversation, the timeline and the
 * internal comments. Actions follow `reviewActions` (packages/shared); the API enforces them.
 */
export function ReviewPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const review = useReview(id);

  if (review.isPending) return <p className="text-fg-muted">{t("app.loading")}</p>;
  if (review.isError) {
    const status = review.error instanceof ApiRequestError ? review.error.status : 0;
    return (
      <section className="rounded-lg bg-surface p-6 shadow-card">
        <p role="alert" className="text-fg">
          {status === 404 ? t("review.notFound") : status === 403 ? t("review.ownRequest") : t("review.loadError")}
        </p>
        <BackLink />
      </section>
    );
  }
  return <Review detail={review.data} />;
}

function Review({ detail }: { detail: ReviewDetail }) {
  const { t, i18n } = useTranslation();
  const catalogue = useFieldCatalogue();
  const catalogueById = new Map(catalogue.data?.fields.map((f) => [f.key, f]));
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" });
  const requester = detail.requester;
  const directory = [requester.company, requester.directoryBusinessUnit, requester.role].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-4">
      <div>
        <BackLink />
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-xl font-bold break-words text-primary sm:text-2xl">
            <span className="tabular">{detail.reference}</span>
            {detail.title ? <span className="font-semibold text-fg"> · {detail.title}</span> : null}
          </h1>
          <RequestStatusBadge status={detail.status} audience="buyer" />
        </div>
        <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <Fact label={t("review.requester")}>
            <span className="text-fg">{requester.displayName}</span>{" "}
            <span className="break-all text-fg-muted">{requester.email}</span>
            {directory ? <div className="text-fg-muted">{directory}</div> : null}
          </Fact>
          <Fact label={t("review.organisation")}>
            {detail.legalEntity || detail.businessUnit ? (
              <>
                <div className="text-fg">{detail.legalEntity?.name ?? "—"}</div>
                <div className="text-fg-muted">{detail.businessUnit?.name ?? "—"}</div>
              </>
            ) : (
              <span className="text-fg-muted">{t("review.unrouted")}</span>
            )}
          </Fact>
          <Fact label={t("review.assignee")}>
            {detail.assignee ? (
              <span className="text-fg">{detail.assignee.displayName}</span>
            ) : (
              <span className="text-fg-muted">{t("review.unassigned")}</span>
            )}
          </Fact>
          <Fact label={t("review.submitted")}>
            <span className="text-fg">
              {detail.submittedAt ? dateFormat.format(new Date(detail.submittedAt)) : "—"}
            </span>
            <div className="text-fg-muted">
              {detail.engagementShape
                ? `${t(`requests.panel.shapes.${detail.engagementShape}`)}${detail.sizeTier ? ` · ${t(`requests.panel.tiers.${detail.sizeTier}`)}` : ""}`
                : t("requests.panel.notClassified")}
            </div>
          </Fact>
        </dl>
      </div>

      <Actions detail={detail} />

      <SourcingSection requestId={detail.id} catalogue={catalogue.data} />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card title={t("review.details")}>
            {detail.fields.length === 0 ? (
              <p className="text-sm text-fg-muted">{t("review.noDetails")}</p>
            ) : (
              <DetailsBySection
                fields={detail.fields}
                catalogue={catalogue.data}
                row={(key, field) => (
                  <FieldRow
                    key={key}
                    requestId={detail.id}
                    fieldKey={key}
                    entry={catalogueById.get(key)}
                    field={field}
                    canEdit={false}
                    editBlocked={false}
                    audience="buyer"
                  />
                )}
              />
            )}
          </Card>
          {detail.documents.length > 0 ? (
            <Card title={t("review.documents")}>
              <DocumentList requestId={detail.id} documents={detail.documents} canRemove={false} removeBlocked />
            </Card>
          ) : null}
          <Transcript detail={detail} />
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Card title={t("review.findings")}>
            {detail.findings.length === 0 ? (
              <p className="text-sm text-fg-muted">{t("review.noFindings")}</p>
            ) : (
              <FindingList findings={detail.findings} catalogue={catalogue.data} audience="buyer" />
            )}
          </Card>
          <Timeline events={detail.events} />
          <Comments detail={detail} />
        </div>
      </div>
    </div>
  );
}

// ── Actions ──────────────────────────────────────────────────────────────────

type Pending = { to: RequestStatus } | null;

function Actions({ detail }: { detail: ReviewDetail }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const transition = useTransition(detail.id);
  const assign = useAssign(detail.id);
  const { actions } = detail;
  const [pending, setPending] = useState<Pending>(null);
  // `n` makes the same message announce and take focus again.
  const [done, setDone] = useState({ text: "", n: 0 });
  // Focus after the commit (a requestAnimationFrame could run before the message is rendered).
  useEffect(() => {
    if (done.n > 0) status.current?.focus();
  }, [done]);
  const status = useRef<HTMLParagraphElement>(null);
  const titleId = useId();
  const busy = transition.isPending || assign.isPending;

  const nothing = !actions.canTake && !actions.canAssign && actions.transitions.length === 0;
  const error = transition.error ?? assign.error;

  function finish(message: string) {
    setPending(null);
    setDone((d) => ({ text: message, n: d.n + 1 }));
  }

  function move(to: RequestStatus, note?: string) {
    transition.mutate(
      { to, ...(note ? { note } : {}) },
      { onSuccess: () => finish(t("review.actions.done", { status: t(`review.status.${to}`) })) },
    );
  }

  return (
    <section aria-labelledby={titleId} className="rounded-lg bg-surface p-4 shadow-card">
      <h2 id={titleId} className="sr-only">
        {t("review.actions.title")}
      </h2>
      {/* Where the request stands, whatever this user may still do (a manager can always reassign). */}
      {detail.status === "APPROVED" || detail.status === "REJECTED" ? (
        <p className="mb-2 text-sm text-fg-muted">{t("review.actions.closed")}</p>
      ) : detail.status === "RETURNED_TO_REQUESTER" ? (
        <p className="mb-2 text-sm text-fg-muted">{t("review.actions.waiting")}</p>
      ) : nothing ? (
        <p className="text-sm text-fg-muted">{t("review.actions.notYours")}</p>
      ) : null}
      {nothing ? null : (
        <div className="flex flex-wrap items-end gap-2">
          {actions.canTake && !actions.transitions.includes("IN_REVIEW") && me.data ? (
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() =>
                me.data && assign.mutate(me.data.id, { onSuccess: () => finish(t("review.actions.taken")) })
              }
            >
              {t("review.actions.take")}
            </Button>
          ) : null}
          {actions.transitions.map((to) => (
            <Button
              key={to}
              variant={
                to === "REJECTED"
                  ? "danger-secondary"
                  : to === "APPROVED" || (to === "IN_REVIEW" && detail.status === "SUBMITTED")
                    ? "primary"
                    : "secondary"
              }
              disabled={busy}
              aria-expanded={NOTE_REQUIRED.has(to) || to === "APPROVED" ? pending?.to === to : undefined}
              onClick={() => (NOTE_REQUIRED.has(to) || to === "APPROVED" ? setPending({ to }) : move(to))}
            >
              {t(
                to === "IN_REVIEW" && detail.status === "SOURCING"
                  ? "review.actions.to.backToReview"
                  : `review.actions.to.${to}`,
              )}
            </Button>
          ))}
          {actions.canAssign ? (
            <AssignControl
              // Re-created when the buyer changes, so the selection shows the current one.
              key={detail.assignee?.id ?? "none"}
              detail={detail}
              busy={busy}
              onAssign={(userId) => assign.mutate(userId, { onSuccess: () => finish(t("review.actions.assigned")) })}
            />
          ) : null}
        </div>
      )}

      {pending ? (
        <NoteForm
          to={pending.to}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={(note) => move(pending.to, note)}
        />
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 rounded-sm bg-danger-bg px-3 py-2 text-sm text-danger">
          {error instanceof ApiRequestError && error.status === 409
            ? t("review.actions.conflict")
            : t("review.actions.failed")}
        </p>
      ) : null}
      <p ref={status} tabIndex={-1} aria-live="polite" className="mt-2 text-sm text-success empty:hidden">
        {done.text}
      </p>
    </section>
  );
}

/** Questions for the requester, a rejection reason, or the approval confirmation. */
function NoteForm({
  to,
  busy,
  onCancel,
  onConfirm,
}: {
  to: RequestStatus;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (note: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [error, setError] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const confirm = useRef<HTMLButtonElement>(null);
  const id = useId();
  const needsNote = NOTE_REQUIRED.has(to);

  useEffect(() => {
    (needsNote ? field.current : confirm.current)?.focus();
  }, [needsNote]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (needsNote && !note.trim()) {
      setError(true);
      field.current?.focus();
      return;
    }
    onConfirm(needsNote ? note.trim() : undefined);
  }

  return (
    <form
      onSubmit={submit}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
      noValidate
      aria-label={t(`review.actions.formTitle.${to}`)}
      className="mt-3 flex flex-col gap-2 rounded-md border border-border p-3"
    >
      <p className="text-sm text-fg">{t(`review.actions.explain.${to}`)}</p>
      {needsNote ? (
        <>
          <label htmlFor={id} className="text-sm font-medium text-fg">
            {t(`review.actions.noteLabel.${to}`)}
          </label>
          <textarea
            ref={field}
            id={id}
            rows={4}
            maxLength={4000}
            value={note}
            aria-invalid={error || undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(event) => {
              setNote(event.target.value);
              setError(false);
            }}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg"
          />
          {error ? (
            <p id={`${id}-error`} className="text-sm text-danger">
              {t("review.actions.noteRequired")}
            </p>
          ) : null}
        </>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button ref={confirm} type="submit" variant={to === "REJECTED" ? "danger" : "primary"} disabled={busy}>
          {t(`review.actions.confirm.${to}`)}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          {t("admin.cancel")}
        </Button>
      </div>
    </form>
  );
}

function AssignControl({
  detail,
  busy,
  onAssign,
}: {
  detail: ReviewDetail;
  busy: boolean;
  onAssign: (userId: string | null) => void;
}) {
  const { t } = useTranslation();
  const assignees = useAssignees(detail.id, true);
  const [choice, setChoice] = useState(detail.assignee?.id ?? "");
  const unchanged = choice === (detail.assignee?.id ?? "");
  return (
    <div className="flex flex-wrap items-end gap-2 sm:ml-auto">
      <SelectField
        id="assign-to"
        label={t("review.actions.assignTo")}
        value={choice}
        placeholder={t("review.unassigned")}
        options={(assignees.data ?? []).map((a) => ({ value: a.id, label: a.displayName }))}
        onChange={(event) => setChoice(event.target.value)}
      />
      <Button variant="secondary" disabled={busy || unchanged} onClick={() => onAssign(choice || null)}>
        {t("review.actions.assign")}
      </Button>
    </div>
  );
}

// ── Side sections ────────────────────────────────────────────────────────────

function Transcript({ detail }: { detail: ReviewDetail }) {
  const { t } = useTranslation();
  const messages = detail.messages.filter((m) => m.status === "COMPLETE");
  return (
    <Card title={t("review.conversation")}>
      {messages.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("review.noMessages")}</p>
      ) : (
        <details>
          <summary className="cursor-pointer text-sm font-semibold text-link">
            {t("review.showConversation", { count: messages.length })}
          </summary>
          <ol className="mt-3 flex flex-col gap-3">
            {messages.map((m) => (
              <li key={m.id} className="text-sm">
                <p className="font-semibold text-fg">
                  {m.role === "USER" ? detail.requester.displayName : t("app.name")}
                </p>
                <p className="whitespace-pre-wrap break-words text-fg">{m.content}</p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </Card>
  );
}

function Timeline({ events }: { events: RequestEvent[] }) {
  const { t, i18n } = useTranslation();
  const format = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const someone = t("review.events.someone");
  const describe = (e: RequestEvent) => {
    const actor = e.actor?.displayName ?? someone;
    if (e.kind === "ASSIGNED") {
      if (!e.assignee) return t("review.events.unassigned", { actor });
      if (e.assignee.id === e.actor?.id) return t("review.events.took", { actor });
      return t("review.events.assigned", { actor, assignee: e.assignee.displayName });
    }
    // Stalled-request steps (ADR-0024) are the system's, never a person's.
    if (e.kind === "REMINDER") return t("review.events.reminder");
    if (e.kind === "FOLLOW_UP") {
      return e.fromStatus === "DRAFT" ? t("review.events.lastReminder") : t("review.events.overdue");
    }
    if (e.toStatus === "ABANDONED") return t("review.events.closed");
    if (e.fromStatus === "ABANDONED") return t("review.events.reopened", { actor });
    if (e.fromStatus === "DRAFT") return t("review.events.submitted", { actor });
    if (e.fromStatus === "RETURNED_TO_REQUESTER") return t("review.events.resubmitted", { actor });
    return t("review.events.moved", { actor, status: t(`review.status.${e.toStatus}`) });
  };
  return (
    <Card title={t("review.history")}>
      {events.length === 0 ? (
        <p className="text-sm text-fg-muted">{t("review.noHistory")}</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {events.map((e) => (
            <li key={e.id} className="border-l-2 border-border pl-3 text-sm">
              <p className="text-fg">{describe(e)}</p>
              {e.note ? <p className="mt-1 whitespace-pre-wrap break-words text-fg-muted">“{e.note}”</p> : null}
              <p className="text-xs text-fg-muted">{format.format(new Date(e.createdAt))}</p>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function Comments({ detail }: { detail: ReviewDetail }) {
  const { t, i18n } = useTranslation();
  const add = useAddComment(detail.id);
  const [body, setBody] = useState("");
  const [error, setError] = useState(false);
  const id = useId();
  const format = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) {
      setError(true);
      return;
    }
    add.mutate(body.trim(), { onSuccess: () => setBody("") });
  }

  return (
    <Card title={t("review.comments")}>
      <p className="mb-2 text-xs text-fg-muted">{t("review.commentsHint")}</p>
      {detail.comments.length > 0 ? (
        <ol className="mb-3 flex flex-col gap-3">
          {detail.comments.map((c) => (
            <li key={c.id} className="text-sm">
              <p className="font-semibold text-fg">
                {c.author?.displayName ?? t("review.events.someone")}{" "}
                <span className="text-xs font-normal text-fg-muted">{format.format(new Date(c.createdAt))}</span>
              </p>
              <p className="whitespace-pre-wrap break-words text-fg">{c.body}</p>
            </li>
          ))}
        </ol>
      ) : null}
      {detail.actions.canComment ? (
        <form onSubmit={submit} noValidate className="flex flex-col gap-2">
          <label htmlFor={id} className="text-sm font-medium text-fg">
            {t("review.addComment")}
          </label>
          <textarea
            id={id}
            rows={3}
            maxLength={4000}
            value={body}
            aria-invalid={error || undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            onChange={(event) => {
              setBody(event.target.value);
              setError(false);
            }}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base text-fg"
          />
          {error ? (
            <p id={`${id}-error`} className="text-sm text-danger">
              {t("review.commentRequired")}
            </p>
          ) : null}
          {add.isError ? (
            <p role="alert" className="text-sm text-danger">
              {t("review.actions.failed")}
            </p>
          ) : null}
          <Button type="submit" variant="secondary" disabled={add.isPending} className="self-start">
            {t("review.postComment")}
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="rounded-lg bg-surface p-4 shadow-card">
      <h2 id={id} className="mb-2 font-heading text-base font-semibold text-fg">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-semibold tracking-wide text-fg-muted uppercase">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function BackLink() {
  const { t } = useTranslation();
  return (
    <Link to="/queue" className="inline-flex items-center gap-1 text-sm font-semibold text-link hover:underline">
      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
      {t("review.backToQueue")}
    </Link>
  );
}
